import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { getUserId } from '@peloton/auth/redux';
import { toImageCdnUrl } from '@engage/image-cdn/toImageCdnUrl';
import { getMember } from '@engage/members/selectors';
import type { MemberSelectorState } from '@engage/members/selectors';
import { useFeatureToggle } from '@members/feature-toggles/featureToggles';
import { Feature } from '@members/feature-toggles/toggles';

export interface BadgeData {
  badgeUrl: string;
  maskUrl: string;
}

export const BADGE_SIZING_CONSTANT = 0.45;
export const MASK_POSITION_CONSTANT = 116;
export const BADGE_POSITION_CONSTANT = 9;

export const LEADERBOARD_BADGE_SIZING_CONSTANT = 0.51;
export const LEADERBOARD_MASK_POSITION_CONSTANT = 127;
export const LEADERBOARD_BADGE_POSITION_CONSTANT = 14;

/*
 * This function finds the static badge url associated with the member and returns the url,
 * dimensions, and positions for the badge and its mask image.
 */
export const useBadgeFromSpriteSheet = (
  showBadge: boolean,
  isSelf?: boolean,
  badgeUrl?: string,
): BadgeData => {
  const currentUserId = useSelector(getUserId);
  const member = useSelector((state: MemberSelectorState) =>
    getMember(state, currentUserId),
  );
  const isClubPelotonEnabled = useFeatureToggle(Feature.RighteousGemstones);

  const [badgeData, setBadgeData] = useState<BadgeData>({
    badgeUrl: '',
    maskUrl: '',
  });

  useEffect(() => {
    if (member && isClubPelotonEnabled && showBadge) {
      // Determine which badge URL to use
      const urlToLoad = isSelf ? member?.badgeUrl ?? '' : badgeUrl ?? '';

      if (urlToLoad) {
        loadBadgeFromUrl(urlToLoad)
          .then(data => setBadgeData(data))
          .catch(error => {
            console.error('Error loading badge:', error);
            setBadgeData({ badgeUrl: '', maskUrl: '' });
          });
      }
    }
  }, [member, isClubPelotonEnabled, showBadge, badgeUrl, isSelf]);

  return badgeData;
};

/**
 * Loads a badge image and returns the processed badge data
 * @param badgeUrl The URL of the badge to load
 * @returns Promise that resolves to BadgeData
 */
const loadBadgeFromUrl = async (badgeUrl: string): Promise<BadgeData> => {
  const badgeNumbers = extractBadgeNumbers(badgeUrl);

  if (!badgeNumbers) {
    return { badgeUrl: '', maskUrl: '' };
  }

  return new Promise((resolve, reject) => {
    const img = new Image();

    img.onload = () => {
      try {
        const calculatedWidth = Math.floor(img.width / badgeNumbers.columns);
        const calculatedHeight = Math.floor(img.height / badgeNumbers.rows);

        const badgeData: BadgeData = {
          badgeUrl: toImageCdnUrl(badgeUrl, {
            height: calculatedHeight,
            width: calculatedWidth,
            x: 0,
            y: 0,
            crop: 'crop',
          }),
          maskUrl: toImageCdnUrl(badgeUrl, {
            height: calculatedHeight,
            width: calculatedWidth,
            x: 0,
            y: calculatedHeight,
            crop: 'crop',
          }),
        };

        const maskImg = new Image();
        maskImg.onload = () => resolve(badgeData);
        maskImg.onerror = () => resolve({ badgeUrl: '', maskUrl: '' });
        maskImg.src = badgeData.maskUrl;
      } catch (error) {
        reject(error);
      }
    };

    img.onerror = () => {
      reject(new Error('Failed to load badge image'));
    };

    img.src = badgeUrl;
  });
};

/**
 * Extract numbers from badge URL by splitting it
 * @param badgeUrl The URL of the badge, in format `domain/path/path/${level}_${rows}_${columns}.png`
 * @returns Array of numbers extracted from the URL
 */
export const extractBadgeNumbers = (
  badgeUrl: string,
): { rows: number; columns: number } | null => {
  // Split the URL by '/' and get the last part
  const urlParts = badgeUrl.split('/');
  const lastPart = urlParts[urlParts.length - 1];

  // Split the last part by '_'
  const substrings = lastPart.split('_');

  // Extract numbers from the substrings
  const dimensions = substrings
    .map(substring => {
      // Parse the substring as a number
      const num = parseInt(substring, 10);
      // Return the number if it's valid, otherwise skip it
      return isNaN(num) ? null : num;
    })
    .filter((num): num is number => num !== null); // Filter out non-number values

  // the url should contain 3 numbers, the level, then rows, then columns
  // we only need rows and columns and if there aren't the expected number of numbers,
  // return null
  return dimensions.length === 3 ? { rows: dimensions[1], columns: dimensions[2] } : null;
};
